import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { AccountInfo, EventMessage, EventType, InteractionStatus, RedirectRequest } from "@azure/msal-browser";
import { environment } from "../../environments/environment";
import { filter, Subject, takeUntil } from "rxjs";

// Defines a token claim.
export class Claim {
  id: number;
  claim: string;
  value: string;
}

// The AuthService acts as a facade over the MSAL services.
export interface IAuthService
{
  // Emits when the login status has changed.
  loginStatusChanged : Subject<boolean>;
  // Performs a login redirect.
  login() : void;
  // Performs a logout redirect.
  logout() : void;
  // Gets a value indicating if a user is logged in.
  isLoggedIn() : boolean;
  // Gets a collection of accounts for the current user.
  getAccounts() : AccountInfo[];
  // Gets the first account for the current user.
  getAccount() : AccountInfo;
  // Gets the first email address associated with the current user.
  getAccountEmailAddress() : string;
  getClaims() : Claim[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements IAuthService, OnDestroy {

  private readonly _destroying$ = new Subject<void>();
  public loginStatusChanged = new Subject<boolean>();

  constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
              private broadcastService: MsalBroadcastService,
              private authService: MsalService) {

    // Subscribe to the broadcast service to listen for changes in authentication state.
    this.broadcastService.inProgress$
        .pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            // No need to unsubscribe, since the subscription will end when the `destroying$` subject emits.
            takeUntil(this._destroying$)
        )
        .subscribe(() => {
          this.loginStatusChanged.next(this.isLoggedIn());
        })

    this.broadcastService.inProgress$
        .pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None || status === InteractionStatus.HandleRedirect),
            takeUntil(this._destroying$)
        )
        .subscribe(() => {
          this.checkAndSetActiveAccount();
          this.loginStatusChanged.next(this.isLoggedIn());
        })

    // Logs a message when login is successful.
    this.broadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        )
        .subscribe((result: EventMessage) => {
          console.log(result);
        });
  }
  ngOnDestroy(): void {
    this.loginStatusChanged.complete();
    
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  login() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout() {
    this.authService.logoutRedirect({
      postLogoutRedirectUri: environment.baseClaimSubmissionWebUrl
    });
  }
  
  getAccounts() : AccountInfo[] {
    return this.authService.instance.getAllAccounts();
  }
  getAccount() : AccountInfo {
    if (this.getAccounts().length == 0) return null;
    else return this.authService.instance.getActiveAccount();
  }

  isLoggedIn(): boolean {
    return this.getAccounts().length > 0;
  }

  getAccountEmailAddress() : string {
    if (this.getAccounts().length == 0) return null;
    else if (this.getAccount().idTokenClaims.emails.length == 0) return null;
    else return this.getAccount().idTokenClaims.emails[0];
  }

  getClaims() : Claim[] {
    if (!this.isLoggedIn()) return [];
    const tokenClaims = this.getAccount()?.idTokenClaims;

    let claims: Claim[] = new Array<Claim>();

    Object.keys(tokenClaims).forEach(function (k, v) {
      let c = new Claim()
      c.id = v;
      c.claim = k;
      c.value = tokenClaims ? (tokenClaims[k] as string) : null;
      claims.push(c);
    });
    return claims;
  }

  private checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }
}
